/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CircleHelp, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { formatStoredDate } from '../../utils/datetime';

const GRID_STROKE = 'color-mix(in srgb, var(--color-border-default) 62%, transparent)';
const AXIS_STROKE = 'var(--color-text-secondary)';
const TOOLTIP_STYLE = {
  backgroundColor: 'var(--color-background-surface)',
  border: '1px solid var(--color-border-default)',
  borderRadius: 8,
  color: 'var(--color-text-primary)',
};
const TOOLTIP_LABEL_STYLE = { color: 'var(--color-primary)' };
const TOOLTIP_ITEM_STYLE = { color: 'var(--color-text-primary)', fontWeight: 600 };
const CHART_TOOLTIP_PROPS = {
  contentStyle: TOOLTIP_STYLE,
  labelStyle: TOOLTIP_LABEL_STYLE,
  itemStyle: TOOLTIP_ITEM_STYLE,
  wrapperClassName: 'sector-chart-tooltip',
};

const PERFORMANCE_WINDOWS = [
  { label: '1D', bars: 1 },
  { label: '1W', bars: 5 },
  { label: '1M', bars: 21 },
  { label: '1Q', bars: 63 },
  { label: '1Y', bars: 252 },
];

const CHART_DESCRIPTIONS = {
  priceRange: 'Shows where the latest close sits inside the available daily price range, using high and low prices from the loaded history.',
  returns: 'Compares close-to-close returns across common lookback windows when enough daily history exists.',
  earnings: 'Plots recent EPS surprise percentages so beats and misses are visible over time.',
  financials: 'Shows revenue, net income, free cash flow, and EPS trends from recent reported statements.',
  valuation: 'Compares the instrument P/E ratio with the sector average when sector analytics are available.',
  dividends: 'Shows recent cash distributions and the implied trailing yield when dividend history exists.',
};

function toNumber(value, fallback = null) {
  if (value == null || value === '') return fallback;
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function getDateValue(row) {
  const raw = row?.timestamp || row?.date || row?.time || row?.ex_dividend_date || row?.report_date;
  if (!raw) return 0;
  if (typeof raw === 'number') return raw > 10000000000 ? raw : raw * 1000;
  const parsed = new Date(raw).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value) {
  if (!value) return '-';
  return formatStoredDate(value, { month: 'short', day: 'numeric', year: 'numeric' }, value);
}

function formatShortDate(value) {
  if (!value) return '-';
  return formatStoredDate(value, { month: 'short', year: '2-digit' }, value);
}

function formatNumber(value, decimals = 2) {
  const num = toNumber(value);
  if (num == null) return '-';
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatPercent(value, signed = false) {
  const num = toNumber(value);
  if (num == null) return '-';
  const sign = signed && num > 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}%`;
}

function formatCompact(value, currency = false) {
  const num = toNumber(value);
  if (num == null) return '-';
  const prefix = currency ? '$' : '';
  const abs = Math.abs(num);
  if (abs >= 1e12) return `${prefix}${(num / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${prefix}${(num / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${prefix}${(num / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${prefix}${(num / 1e3).toFixed(1)}K`;
  return `${prefix}${num.toLocaleString(undefined, { maximumFractionDigits: currency ? 2 : 0 })}`;
}

function changeColor(value) {
  const num = toNumber(value, 0);
  if (num > 0) return 'var(--color-success)';
  if (num < 0) return 'var(--color-destructive)';
  return 'var(--color-text-secondary)';
}

function fiscalSortValue(row) {
  const year = toNumber(row?.fiscal_year, 0);
  const period = String(row?.fiscal_period || '').toUpperCase();
  const periodRank = period === 'FY' ? 5 : toNumber(period.replace(/\D/g, ''), 0);
  return year * 10 + periodRank;
}

function periodLabel(row) {
  const period = row?.fiscal_period || '';
  const year = row?.fiscal_year || '';
  return [period, year].filter(Boolean).join(' ') || formatShortDate(row?.report_date || row?.filing_date);
}

function chartTooltipFormatter(value, name) {
  const label = String(name || '');
  if (label.includes('%') || label.includes('Return') || label.includes('Surprise')) {
    return [formatPercent(value, label.includes('Return') || label.includes('Surprise')), label];
  }
  if (label.includes('P/E')) return [formatNumber(value, 1), label];
  if (label.includes('EPS')) return [formatNumber(value, 2), label];
  if (label.includes('Dividend')) return [`$${formatNumber(value, 4)}`, label];
  return [formatCompact(value, true), label];
}

function ChartInfoTooltip({ children }) {
  return (
    <span className="sector-chart-info">
      <button type="button" className="sector-chart-info-button" aria-label="Chart explanation">
        <CircleHelp className="h-3.5 w-3.5" />
      </button>
      <span className="sector-chart-info-tooltip" role="tooltip">
        {children}
      </span>
    </span>
  );
}

function ChartPanel({ title, description, children, className = '' }) {
  return (
    <Card className={`explorer-section-card rounded-lg ${className}`}>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="text-sm font-semibold text-textPrimary">{title}</h2>
          {description && <ChartInfoTooltip>{description}</ChartInfoTooltip>}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyChart({ children = 'No data available.' }) {
  return (
    <div className="instrument-empty-chart">
      <p>{children}</p>
    </div>
  );
}

function MiniStat({ label, value, tone = '' }) {
  return (
    <div className="instrument-mini-stat">
      <span>{label}</span>
      <strong className={tone}>{value}</strong>
    </div>
  );
}

function PriceRangePanel({ range }) {
  if (!range) {
    return (
      <ChartPanel title="Price Range" description={CHART_DESCRIPTIONS.priceRange}>
        <EmptyChart>No daily price history available.</EmptyChart>
      </ChartPanel>
    );
  }

  return (
    <ChartPanel title="Price Range" description={CHART_DESCRIPTIONS.priceRange}>
      <div className="instrument-range-card">
        <div className="instrument-range-summary">
          <div>
            <span>Latest Close</span>
            <strong>{formatNumber(range.current)}</strong>
          </div>
          <div>
            <span>Range Position</span>
            <strong>{formatPercent(range.position, false)}</strong>
          </div>
        </div>
        <div className="instrument-range-gauge" aria-label="Price range position">
          <div className="instrument-range-track">
            <div className="instrument-range-fill" style={{ width: `${range.position}%` }} />
            <span className="instrument-range-marker" style={{ left: `${range.position}%` }} />
          </div>
          <div className="instrument-range-labels">
            <span>{formatNumber(range.low)}</span>
            <span>{formatNumber(range.high)}</span>
          </div>
        </div>
        <div className="instrument-mini-grid">
          <MiniStat label="From High" value={formatPercent(range.fromHigh, true)} tone={range.fromHigh >= 0 ? 'text-success' : 'text-destructive'} />
          <MiniStat label="From Low" value={formatPercent(range.fromLow, true)} tone={range.fromLow >= 0 ? 'text-success' : 'text-destructive'} />
          <MiniStat label="Daily Bars" value={range.count.toLocaleString()} />
          <MiniStat label="History" value={`${formatDate(range.startDate)} - ${formatDate(range.endDate)}`} />
        </div>
      </div>
    </ChartPanel>
  );
}

function ReturnsPanel({ returns }) {
  return (
    <ChartPanel title="Period Returns" description={CHART_DESCRIPTIONS.returns}>
      <div className="instrument-return-strip">
        {returns.map((item) => {
          const available = item.returnPct != null;
          const magnitude = available ? Math.min(100, Math.abs(item.returnPct) * 8) : 0;
          return (
            <div key={item.label} className="instrument-return-row">
              <span className="instrument-return-label">{item.label}</span>
              <div className="instrument-return-bar">
                <div className="instrument-return-midline" />
                {available && (
                  <div
                    className={`instrument-return-fill ${item.returnPct >= 0 ? 'instrument-return-fill-positive' : 'instrument-return-fill-negative'}`}
                    style={{
                      width: `${magnitude / 2}%`,
                      left: item.returnPct >= 0 ? '50%' : `${50 - magnitude / 2}%`,
                    }}
                  />
                )}
              </div>
              <strong className={`instrument-return-value ${available ? item.returnPct >= 0 ? 'text-success' : 'text-destructive' : 'text-textSecondary'}`}>
                {available ? formatPercent(item.returnPct, true) : '-'}
              </strong>
            </div>
          );
        })}
      </div>
    </ChartPanel>
  );
}

function EarningsPanel({ data }) {
  return (
    <ChartPanel title="Earnings Surprise" description={CHART_DESCRIPTIONS.earnings}>
      <div className="h-[300px]">
        {data.length === 0 ? (
          <EmptyChart>No earnings surprise history available.</EmptyChart>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 18, bottom: 14, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="label" stroke={AXIS_STROKE} tick={{ fontSize: 11 }} />
              <YAxis stroke={AXIS_STROKE} tick={{ fontSize: 11 }} tickFormatter={(value) => `${value}%`} />
              <Tooltip formatter={chartTooltipFormatter} {...CHART_TOOLTIP_PROPS} />
              <ReferenceLine y={0} stroke={AXIS_STROKE} strokeOpacity={0.65} />
              <Bar dataKey="surprisePct" name="EPS Surprise %" radius={[4, 4, 0, 0]}>
                {data.map((entry) => (
                  <Cell key={`eps-${entry.label}`} fill={changeColor(entry.surprisePct)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </ChartPanel>
  );
}

function FinancialTrendsPanel({ data }) {
  return (
    <ChartPanel title="Financial Trends" description={CHART_DESCRIPTIONS.financials}>
      <div className="h-[340px]">
        {data.length === 0 ? (
          <EmptyChart>No financial statements available.</EmptyChart>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 14, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="label" stroke={AXIS_STROKE} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="money" stroke={AXIS_STROKE} tick={{ fontSize: 11 }} tickFormatter={(value) => formatCompact(value, true)} />
              <YAxis yAxisId="eps" orientation="right" stroke={AXIS_STROKE} tick={{ fontSize: 11 }} tickFormatter={(value) => formatNumber(value, 1)} />
              <Tooltip formatter={chartTooltipFormatter} {...CHART_TOOLTIP_PROPS} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="money" dataKey="revenue" name="Revenue" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="money" dataKey="netIncome" name="Net Income" fill="#14b8a6" radius={[4, 4, 0, 0]} />
              <Line yAxisId="money" type="monotone" dataKey="freeCashFlow" name="Free Cash Flow" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="eps" type="monotone" dataKey="eps" name="EPS" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </ChartPanel>
  );
}

function ValuationPanel({ data, sectorName, loading, error, marketCapShare }) {
  return (
    <ChartPanel title="Valuation Context" description={CHART_DESCRIPTIONS.valuation}>
      <div className="h-[260px]">
        {loading ? (
          <div className="instrument-empty-chart">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p>Loading sector context...</p>
          </div>
        ) : data.length === 0 ? (
          <EmptyChart>No positive EPS valuation data available.</EmptyChart>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 8, right: 18, bottom: 12, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
              <XAxis type="number" stroke={AXIS_STROKE} tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" width={116} stroke={AXIS_STROKE} tick={{ fontSize: 11 }} />
              <Tooltip formatter={chartTooltipFormatter} {...CHART_TOOLTIP_PROPS} />
              <Bar dataKey="pe" name="P/E" radius={[0, 4, 4, 0]}>
                {data.map((entry) => (
                  <Cell key={`pe-${entry.name}`} fill={entry.kind === 'instrument' ? 'var(--color-primary)' : '#14b8a6'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="instrument-mini-grid mt-3">
        <MiniStat label="Sector" value={sectorName || '-'} />
        <MiniStat label="Sector Share" value={marketCapShare == null ? '-' : formatPercent(marketCapShare)} />
      </div>
      {error && <p className="mt-3 text-xs text-textSecondary">Sector comparison unavailable.</p>}
    </ChartPanel>
  );
}

function DividendsPanel({ data, trailingTotal, trailingYield }) {
  return (
    <ChartPanel title="Dividends" description={CHART_DESCRIPTIONS.dividends}>
      <div className="h-[260px]">
        {data.length === 0 ? (
          <EmptyChart>No dividend history available.</EmptyChart>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 18, bottom: 14, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="label" stroke={AXIS_STROKE} tick={{ fontSize: 11 }} />
              <YAxis stroke={AXIS_STROKE} tick={{ fontSize: 11 }} tickFormatter={(value) => `$${formatNumber(value, 2)}`} />
              <Tooltip formatter={chartTooltipFormatter} {...CHART_TOOLTIP_PROPS} />
              <Bar dataKey="amount" name="Dividend Amount" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      {data.length > 0 && (
        <div className="instrument-mini-grid mt-3">
          <MiniStat label="Trailing Total" value={`$${formatNumber(trailingTotal, 2)}`} />
          <MiniStat label="Trailing Yield" value={trailingYield == null ? '-' : formatPercent(trailingYield)} />
        </div>
      )}
    </ChartPanel>
  );
}

export default function InstrumentVisuals({
  symbol,
  sym,
  profile,
  prices,
  financials,
  earnings,
  dividends,
  sectorAnalytics,
  sectorAnalyticsLoading,
  sectorAnalyticsError,
}) {
  const sortedPrices = useMemo(() => (
    [...(prices || [])]
      .filter((row) => toNumber(row?.close) != null)
      .sort((a, b) => getDateValue(a) - getDateValue(b))
  ), [prices]);

  const latestPrice = sortedPrices.at(-1);
  const latestClose = toNumber(latestPrice?.close);

  const priceRange = useMemo(() => {
    if (sortedPrices.length === 0 || latestClose == null) return null;
    const high = Math.max(...sortedPrices.map((row) => toNumber(row.high ?? row.close, latestClose)));
    const low = Math.min(...sortedPrices.map((row) => toNumber(row.low ?? row.close, latestClose)));
    const span = high - low;
    const position = span > 0 ? Math.max(0, Math.min(100, ((latestClose - low) / span) * 100)) : 50;
    return {
      high,
      low,
      current: latestClose,
      position,
      fromHigh: high ? ((latestClose - high) / high) * 100 : null,
      fromLow: low ? ((latestClose - low) / low) * 100 : null,
      count: sortedPrices.length,
      startDate: sortedPrices[0]?.timestamp || sortedPrices[0]?.date,
      endDate: latestPrice?.timestamp || latestPrice?.date,
    };
  }, [latestClose, latestPrice, sortedPrices]);

  const returns = useMemo(() => (
    PERFORMANCE_WINDOWS.map((window) => {
      if (sortedPrices.length <= window.bars || latestClose == null) {
        return { ...window, returnPct: null };
      }
      const prior = sortedPrices[sortedPrices.length - 1 - window.bars];
      const priorClose = toNumber(prior?.close);
      if (!priorClose) return { ...window, returnPct: null };
      return {
        ...window,
        returnPct: ((latestClose - priorClose) / priorClose) * 100,
      };
    })
  ), [latestClose, sortedPrices]);

  const earningsData = useMemo(() => (
    [...(earnings || [])]
      .map((row) => {
        const estimate = toNumber(row.eps_estimate);
        const surprise = toNumber(row.eps_surprise);
        const surprisePct = toNumber(row.eps_surprise_pct, estimate && surprise != null ? (surprise / Math.abs(estimate)) * 100 : null);
        return {
          label: periodLabel(row),
          dateValue: getDateValue(row),
          surprisePct,
        };
      })
      .filter((row) => row.surprisePct != null)
      .sort((a, b) => a.dateValue - b.dateValue)
      .slice(-8)
  ), [earnings]);

  const financialTrendData = useMemo(() => {
    const incomeRows = [...(financials || [])]
      .filter((row) => row.statement_type === 'income')
      .sort((a, b) => fiscalSortValue(a) - fiscalSortValue(b))
      .slice(-8);
    const cashRows = new Map(
      (financials || [])
        .filter((row) => row.statement_type === 'cash_flow')
        .map((row) => [`${row.fiscal_year}-${row.fiscal_period}`, row])
    );

    return incomeRows.map((row) => {
      const cash = cashRows.get(`${row.fiscal_year}-${row.fiscal_period}`);
      return {
        label: periodLabel(row),
        revenue: toNumber(row.revenue, 0),
        netIncome: toNumber(row.net_income, 0),
        freeCashFlow: toNumber(cash?.free_cash_flow, null),
        eps: toNumber(row.eps_basic, null),
      };
    });
  }, [financials]);

  const latestIncome = useMemo(() => (
    [...(financials || [])]
      .filter((row) => row.statement_type === 'income')
      .sort((a, b) => fiscalSortValue(b) - fiscalSortValue(a))[0]
  ), [financials]);

  const instrumentPe = useMemo(() => {
    const eps = toNumber(latestIncome?.eps_basic);
    if (latestClose == null || eps == null || eps <= 0) return null;
    return latestClose / eps;
  }, [latestClose, latestIncome]);

  const sectorName = profile?.sector || sym?.sector || '';
  const sectorRow = useMemo(() => {
    const sectors = sectorAnalytics?.sectors || [];
    return sectors.find((row) => row.sector === sectorName) || sectors[0] || null;
  }, [sectorAnalytics, sectorName]);
  const sectorPe = toNumber(sectorRow?.avg_pe_ratio ?? sectorRow?.median_pe_ratio);
  const valuationData = [
    instrumentPe != null ? { name: symbol, pe: instrumentPe, kind: 'instrument' } : null,
    sectorPe != null && sectorPe > 0 ? { name: sectorName || 'Sector', pe: sectorPe, kind: 'sector' } : null,
  ].filter(Boolean);
  const marketCap = toNumber(profile?.market_cap);
  const sectorMarketCap = toNumber(sectorRow?.total_market_cap);
  const marketCapShare = marketCap != null && sectorMarketCap ? (marketCap / sectorMarketCap) * 100 : null;

  const dividendData = useMemo(() => (
    [...(dividends || [])]
      .map((row) => ({
        label: formatShortDate(row.ex_dividend_date || row.pay_date),
        dateValue: getDateValue(row),
        amount: toNumber(row.cash_amount, 0),
      }))
      .filter((row) => row.amount > 0)
      .sort((a, b) => a.dateValue - b.dateValue)
      .slice(-12)
  ), [dividends]);
  const trailingDividendTotal = dividendData.reduce((sum, row) => sum + row.amount, 0);
  const trailingDividendYield = latestClose && trailingDividendTotal ? (trailingDividendTotal / latestClose) * 100 : null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        <PriceRangePanel range={priceRange} />
        <ReturnsPanel returns={returns} />
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <FinancialTrendsPanel data={financialTrendData} />
        <EarningsPanel data={earningsData} />
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <ValuationPanel
          data={valuationData}
          sectorName={sectorName}
          loading={sectorAnalyticsLoading}
          error={sectorAnalyticsError}
          marketCapShare={marketCapShare}
        />
        <DividendsPanel
          data={dividendData}
          trailingTotal={trailingDividendTotal}
          trailingYield={trailingDividendYield}
        />
      </div>
    </div>
  );
}
